import { useEffect } from 'react';
import { useStore } from '@/lib/store';
import { useLocation, useSearchParams } from 'react-router-dom';
import { MobileStories } from './MobileStories';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { uploadLocalDataToCloud } from '@/services/cloudSync';
import { isBootstrapPendingForUser } from '@/services/syncLifecycle';
import {
    isWalkthroughSuppressed,
    suppressWalkthrough,
} from '@/lib/first-run-preferences';

// Consolidated Welcome Flow Controller
// Replaces the old Joyride walkthrough with a unified logic:
// - Mobile: Renders <MobileStories />
// - Desktop: Triggers the Discovery Tour (managed by discovery-slice)
export function Walkthrough() {
    const isMobile = useMediaQuery('(max-width: 1024px)');
    const hasSeenWalkthrough = useStore(state => state.preferences?.hasSeenWalkthrough);
    const setPreferences = useStore(state => state.setPreferences);

    const setDiscoveryActive = useStore(state => state.setDiscoveryActive);
    const setDiscoveryStep = useStore(state => state.setDiscoveryStep);
    const resetDiscoveryProgress = useStore(state => state.resetDiscoveryProgress);
    const discoveryActive = useStore(state => state.discovery.active);
    const isHydrating = useStore(state => state.isHydrating);
    const authUser = useStore(state => state.authUser);
    const athleteName = useStore(state => state.athlete?.name);

    const consent = useStore(state => state.consent);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const bootstrapPending = isBootstrapPendingForUser(authUser?.id);

    // The walkthrough demos in-app features (dashboard, schedule, etc.) and
    // imperatively navigates to /dashboard via MobileStories. It must never run
    // for unauthenticated users or before onboarding completes — otherwise the
    // forced /dashboard navigation collides with ProtectedRoute redirecting to
    // /login (or /onboarding), producing a navigation loop that eventually
    // crashes the router into the Engine Overheat fallback.
    const canRunWalkthrough = Boolean(authUser?.id) && Boolean(athleteName);

    // 1. Handle "Onboarding Complete" Signal
    useEffect(() => {
        if (isHydrating || bootstrapPending) return;
        if (!canRunWalkthrough) return;
        if (isWalkthroughSuppressed()) return;
        if (searchParams.get('onboarding') === 'complete') {
            console.log('[Walkthrough] Onboarding completion detected');

            // Clean URL
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('onboarding');
            setSearchParams(newParams, { replace: true });

            // Reset Tour State (Fresh Start)
            setDiscoveryStep(0);
            resetDiscoveryProgress();
            setPreferences({ hasSeenWalkthrough: false });

            // Trigger appropriate flow
            if (!isMobile) {
                setDiscoveryActive(true);
            }
        }
    }, [bootstrapPending, isHydrating, canRunWalkthrough, searchParams, setSearchParams, isMobile, setDiscoveryActive, setDiscoveryStep, resetDiscoveryProgress, setPreferences]);

    // 2. Resilience: Allow Tour to start if user hasn't seen it and conditions are met
    // (e.g. they refreshed the page, or navigated away and came back)
    useEffect(() => {
        if (isHydrating || bootstrapPending) return;
        if (!canRunWalkthrough) return;
        const isOnboarding = location.pathname.includes('/onboarding');

        // Conditions to start:
        // - Not seen yet
        // - Consent dismissed (so we don't overlap legal banners)
        // - Not currently on onboarding page
        // - Discovery not already active
        const shouldStart =
            !isWalkthroughSuppressed() &&
            hasSeenWalkthrough === false &&
            consent.dismissed &&
            !isOnboarding &&
            !discoveryActive;

        if (shouldStart) {
            if (!isMobile) {
                // Desktop: Auto-trigger Discovery Tour
                // Small delay to ensure layout is ready
                const timer = setTimeout(() => {
                    setDiscoveryActive(true);
                }, 1000);
                return () => clearTimeout(timer);
            }
        }
    }, [
        isHydrating,
        bootstrapPending,
        canRunWalkthrough,
        hasSeenWalkthrough,
        consent.dismissed,
        location.pathname,
        discoveryActive,
        isMobile,
        setDiscoveryActive
    ]);

    // 3. Render Mobile Flow directly
    // (Desktop flow is rendered via GhostOverlay in layout.tsx)
    if (
        isMobile &&
        canRunWalkthrough &&
        !isHydrating &&
        !bootstrapPending &&
        !isWalkthroughSuppressed() &&
        hasSeenWalkthrough === false &&
        !location.pathname.includes('/onboarding')
    ) {
        return (
            <MobileStories
                onComplete={() => {
                    setPreferences({ hasSeenWalkthrough: true });
                    suppressWalkthrough();
                    uploadLocalDataToCloud({ includeSettings: true, includeProfile: true }).catch(console.error);
                }}
            />
        );
    }

    return null;
}
